import { is } from 'immutable';
import { createSelector } from 'reselect';

import { FAVORITE_SEARCH_SLOTS } from './constants';


const FavoriteSlots = Array.from({ length: FAVORITE_SEARCH_SLOTS }, (v, i) => i + 1);


const checkState = state => state;

const selectSearch = createSelector(checkState, state => state.get('search'));

export const selectLoading = createSelector(selectSearch, search => search.get('loading'));

export const selectStale = createSelector(selectSearch, search => search.get('stale'));

// Bumped once per listing search that actually returned results — a change in
// this value is what proves the active context's resultCount belongs to the
// search you triggered, rather than being left over from the previous one.
export const selectListingResultVersion = createSelector(selectSearch, search => search.get('listingResultVersion'));

// { status, timeout, at } for the last failed listing search, or null.
export const selectListingError = createSelector(selectSearch, search => search.get('listingError'));

export const selectSuggestion = createSelector(selectSearch, search => search.get('suggestion'));

export const selectAvailableListingTypes = createSelector(selectSearch, search => search.get('availableListingTypes'));

export const selectSuggestionValue = createSelector(selectSuggestion, suggestion => suggestion.get('value'));

export const selectLocation = createSelector(selectSearch, search => search.get('location'));

export const selectFilter = createSelector(selectSearch, search => search.get('filter'));

export const selectresetPasswordSuccess = createSelector(
  state => state.get('user'),
  user => user.get('redirectMessage')
);

export const selectNewFilter = createSelector(
  selectSearch,
  search => search.get('filter').toJS() // Convert to plain JS object
);

export const selectFilterByKey = createSelector(
  selectFilter,
  (_, key) => key, // Pass the key as the second argument
  (filter, key) => {
    if (key) {
      return { key, value: filter.get(key) };
    } 
  }
);


export const selectResult = createSelector(selectSearch, search => search.get('result'));

export const selectQuery = createSelector(selectResult, search => search.get('query'));

// Define a selector that selects the 'localFilter' from the 'search' slice of state
export const selectLocalFilter = createSelector(
    selectSearch,
    search =>  search.get('localFilter')
  );
  
// Define a selector that filters 'localFilter' by ID
export const selectLocalFilterById = createSelector(
    selectLocalFilter,
    (_, id) => id,
    (localFilter, id) => {
      // Filter 'localFilter' by the provided ID
      return localFilter && localFilter.length && localFilter.filter(item => item?.key === id);
    }
  );

export const selectActiveContext = createSelector(selectResult, result => result.getIn(['contexts', result.get('activeContext')], null));

export const selectMap = createSelector(selectSearch, search => search.get('map'));

export const selectMapMetrics = createSelector(selectMap, map => map.get('metrics'));

export const selectStatistic = createSelector(selectSearch, map => map.get('statistic'));

export const selectSavedSearches = createSelector(selectSearch, map => map.get('savedSearches'));

export const selectSavedSearchLoading = createSelector(selectSearch, map => map.get('savedSearchLoading'));

export const selectFavoriteSearches = createSelector(selectSearch, map => map.get('favoriteSearches'));

export const selectFavoriteSearchCounts = createSelector(selectSearch, map => map.get('favoriteSearchCounts'));

export const selectFavoriteSearchLoading = createSelector(selectSearch, map => map.get('favoriteSearchLoading'));

export const selectFavoriteSearchStale = createSelector(selectSearch, map => map.get('favoriteSearchStale'));

export const selectFavoriteSearchSlots = createSelector([selectFavoriteSearches, selectFavoriteSearchCounts], (favoriteSearches, favoriteCounts) => FavoriteSlots.map(s => ({ slot: s, search: favoriteSearches.get(String(s)) })).map(s => ({ ...s, quantity: favoriteCounts.get(String(s.search && s.search.get('id')), 0) })));

export const selectFavoriteSearchSlotOptions = createSelector(selectFavoriteSearchSlots, slots => slots.map(s => ({ value: s.slot, ...(s.search ? { id: s.search.get('id'), label: s.search.get('name') } : { id: null, label: '-Empty-' }) })));
export const selectShowAllEditOption = createSelector(selectSearch, map => map.get('showAllEditOption'));

export const selectLeadList = createSelector(selectSearch, map => map.get('leadeList'));

export const selectSelectedsavesearch = createSelector(selectSearch, filter => filter.get('selectedsavesearch'));

export const selectFavoriteSelectedName = createSelector(selectSearch, filter => filter.get('favoriteSelectedName'));

// ATAI-286: identifies the searched place by ONE id, picked in setLocation's
// own precedence (cityId > countyId > stateId > fips > zip, shapeDefinition at
// the top level). setLocation keeps that id under `geo`; the property
// open/back flows write ids under `query`, which also carries extras such as
// fips, so comparing every id would key the same place differently. A drawn
// area leaves `geo` as an empty Map. null when there is no searchable place.
const AI_SEARCH_GEO_PRECEDENCE = ['cityId', 'countyId', 'stateId', 'fips', 'zip'];
const aiSearchLocationKey = (location) => {
  if (!location) return null;
  const shape = location.get('shapeDefinition');
  if (shape) return `shapeDefinition:${shape}`;
  const ids = [location.get('geo'), location.get('query')]
    .find(src => src && AI_SEARCH_GEO_PRECEDENCE.some(field => src.get(field)));
  const key = ids && AI_SEARCH_GEO_PRECEDENCE.find(field => ids.get(field));
  return key ? `${key}:${ids.get(key)}` : null;
};

// ATAI-286: the summary of the AI Search search the grid is showing, but only
// once a chat turn has ended without running a new search, and only while the
// filter and location still match what that search applied. Anything else
// (a manual filter edit, the location bar, a saved search, Clear All) means
// the summary no longer describes the grid, so nothing is shown.
export const selectEarlierAiSearchSummary = createSelector(selectSearch, (search) => {
  const label = search.get('aiSearchLabel');
  if (!label || !label.get('earlier')) return null;
  if (!is(label.get('filter'), search.get('filter'))) return null;
  const locationKey = aiSearchLocationKey(label.get('location'));
  if (!locationKey || locationKey !== aiSearchLocationKey(search.get('location'))) return null;
  return label.get('summary');
});


export default selectSearch;
